<template>
  <main
    class="flex sm:flex-col-reverse sm:overflow-y-auto md:h-full md:max-h-full md:flex-row md:overflow-hidden"
  >
    <Details
      :claims="claims ?? []"
      class="shrink-0 md:basis-[30%]"
      :customerRequest
    />

    <div
      class="flex flex-col gap-6 px-24 pb-24 md:h-full md:grow"
      data-test="conversation-wrapper"
    >
      <div ref="scrollContainer" class="grow overflow-y-auto">
        <div v-if="isPending || claimsError || !claims">
          <div
            v-if="isPending"
            class="flex w-full grow flex-col items-center p-16"
          >
            <ConversationSkeleton />
          </div>
          <ErrorInfoBlock v-else class="mt-24" @try-again="refreshClaims" />
        </div>

        <div v-else class="mb-6 space-y-24">
          <Conversation v-for="claim in claims" :key="claim.claimId" :claim />
        </div>
      </div>

      <ActionBar
        :customerRequestId="customerRequest.customerRequestId"
        data-test="conversation-seller-action-bar"
        :hasActiveInsurancePolicy="customerRequest.hasActiveInsurancePolicy"
        :orderlineId="customerRequest.orderline.orderlineId"
        :orderlinePublicId="customerRequest.orderline.publicId"
        :orderlineQuantity="customerRequest.orderline.quantity"
        :product-return-state="customerRequest.productReturnState"
        @item-shipped-back="refreshClaims"
        @message-sent="refreshClaimsAndCustomerRequest"
        @problem-reported="refreshClaims"
        @refund-created="refreshClaimsAndCustomerRequest"
      />
    </div>
  </main>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import type { CareFolder } from '~/api/care-folders/types/care-folder'

import ErrorInfoBlock from '../../../../components/ErrorInfoBlock/ErrorInfoBlock.vue'

import ActionBar from './components/ActionBar/ActionBar.vue'
import Conversation from './components/Conversation/Conversation.vue'
import ConversationSkeleton from './components/Conversation/ConversationSkeleton.vue'
import Details from './components/Details/Details.vue'

const $httpFetch = useHttpFetch()

const emit = defineEmits<(e: 'refresh-customer-request') => void>()

const props = defineProps<{
  customerRequest: CareFolder
}>()

const scrollContainer = ref<HTMLDivElement | null>(null)

const {
  data: claims,
  refresh: refreshClaims,
  error: claimsError,
  status,
} = useAsyncData(() =>
  $httpFetch('/bm/customer_request/:customerRequestId/merchant/discussion', {
    pathParams: { customerRequestId: props.customerRequest.customerRequestId },
  }),
)
const isPending = computed(() => status.value === 'pending')

function scrollToLastMessage({ smoothly }: { smoothly?: boolean } = {}) {
  if (scrollContainer.value?.scrollTo) {
    scrollContainer.value.scrollTo({
      top: scrollContainer.value.scrollHeight,
      behavior: smoothly ? 'smooth' : 'instant',
    })
  }
}

function refreshClaimsAndCustomerRequest() {
  refreshClaims()
  emit('refresh-customer-request')
}

onMounted(() => {
  scrollToLastMessage()
})

watch(isPending, async () => {
  if (!isPending.value) {
    await nextTick()
    scrollToLastMessage()
  }
})
</script>
